import { motion } from "framer-motion";
import { Play, Smartphone, Store, Landmark } from "lucide-react";

const Film = () => {
  return (
    <section
      id="film"
      data-testid="film-section"
      className="relative py-24 sm:py-32 bg-[#0c0a09] border-y border-amber-500/20 overflow-hidden"
    >
      {/* cinematic backdrop — AI style frame from the film directive */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25 pointer-events-none"
        style={{ backgroundImage: "url(/assets/film/sc01-coldopen.png)" }}
        aria-hidden="true"
      />
      {/* drifting gold motes */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-amber-500"
            style={{
              width: i % 3 === 0 ? 3 : 2,
              height: i % 3 === 0 ? 3 : 2,
              left: `${(i * 47) % 100}%`,
              top: `${(i * 61) % 100}%`,
              opacity: 0.2 + (i % 4) * 0.12,
              animation: `film-float ${8 + (i % 5)}s ease-in-out ${i * 0.4}s infinite`,
            }}
          />
        ))}
        <style>{`@keyframes film-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-38px) } }`}</style>
      </div>

      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.p
          className="text-[11px] uppercase tracking-[0.4em] text-amber-500 mb-6"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
        >
          The Institute Presents
        </motion.p>

        <motion.h2
          className="text-4xl sm:text-6xl font-black tracking-tight text-stone-100 leading-[0.95]"
          style={{ fontFamily: "Georgia, serif" }}
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.7 }}
        >
          THE CASH <span className="text-amber-500">VACUUM</span>
        </motion.h2>

        <motion.p
          className="mt-6 text-stone-400 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
        >
          An interactive film in ten scenes: watch physical cash become digital dollars,
          move person to person for free, and come back out as bills — painted from the
          live system, signed in hardware.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.35, type: "spring", stiffness: 90, damping: 14 }}
          className="mt-10"
        >
          <a
            href="/film"
            data-testid="film-watch-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-amber-500 text-black font-bold text-sm uppercase tracking-wider px-8 py-4 hover:bg-amber-400 transition-colors"
          >
            <span className="w-8 h-8 rounded-full bg-black/20 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play size={14} fill="currentColor" />
            </span>
            Watch the Film
          </a>
          <a
            href="/guide"
            data-testid="film-manual-cta"
            className="mt-4 inline-block text-[11px] uppercase tracking-[0.3em] text-stone-500 hover:text-amber-500 transition-colors"
          >
            or read the interactive viewing manual →
          </a>
          <a
            href="/assets/film/bgnzn-sizzle-90s.mp4"
            download
            data-testid="film-sizzle-download"
            className="mt-2 inline-block text-[11px] uppercase tracking-[0.3em] text-violet-400/80 hover:text-violet-300 transition-colors"
          >
            download the 90-sec sizzle reel ↓
          </a>
          <a
            href="/assets/film/bgnzn-sizzle-90s-vertical.mp4"
            download
            data-testid="film-sizzle-vertical-download"
            className="mt-2 inline-block text-[11px] uppercase tracking-[0.3em] text-violet-400/80 hover:text-violet-300 transition-colors"
          >
            vertical cut for reels/tiktok ↓
          </a>
        </motion.div>

        <motion.div
          className="mt-12 flex flex-wrap justify-center gap-3 text-[10px] uppercase tracking-[0.25em]"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
        >
          {[
            { icon: Smartphone, label: "Members — the wallet" },
            { icon: Store, label: "Shops — the register" },
            { icon: Landmark, label: "Desks — the portal" },
          ].map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-2 rounded-full border border-stone-700 text-stone-400 px-4 py-2">
              <Icon size={12} className="text-amber-500" /> {label}
            </span>
          ))}
        </motion.div>

        <p className="mt-10 text-stone-600 text-xs font-mono tracking-[0.3em]">
          CASH IN · MOVE FREE · CASH OUT — CHAIN 9940
        </p>
      </div>
    </section>
  );
};

export default Film;
